import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import { Footer } from "../components/Contact";
import { SITE } from "../config";

const UPDATED = "June 2026";

const emails = (SITE.BUSINESS_EMAILS || []).map((e) => e.trim()).filter(Boolean);
const address = (SITE.BUSINESS_ADDRESS || "").trim();

const SECTIONS = [
  {
    h: "1. Introduction",
    p: [
      "Arco Orbis2 (\"we\", \"us\", \"our\") is committed to protecting your privacy and handling your personal information responsibly, lawfully and transparently. This Privacy Policy explains what information we collect when you use this website, how we use it, and the choices and rights you have.",
      "By using this website or submitting an enquiry, you acknowledge that you have read and understood this policy.",
    ],
  },
  {
    h: "2. Information We Collect",
    list: [
      "Contact enquiry details you provide via our enquiry form: your name, organisation, email address, telephone number, selected service and message.",
      "Account information if you create an account: your name, email address and a securely hashed password.",
      "Advisory chat messages you send to our on-site \"Risk Advisor\" assistant.",
      "Basic technical information such as your browser type and general usage of the site, used only to keep the service secure and working correctly.",
    ],
  },
  {
    h: "3. How We Use Your Information",
    list: [
      "To respond to your enquiry and discuss your security, risk or advisory requirements.",
      "To provide, maintain and secure your account and the website.",
      "To operate the advisory chat feature and provide general, high-level guidance.",
      "To protect the website against fraud, abuse and unauthorised access.",
    ],
    p: [
      "We do not sell your personal information, and we do not use it for unrelated marketing without your consent.",
    ],
  },
  {
    h: "4. Cookies & Local Storage",
    p: [
      "This website uses a small amount of browser storage that is strictly necessary for it to function — for example, to keep you signed in to your account and to maintain your chat session. We do not use advertising or third-party tracking cookies.",
    ],
  },
  {
    h: "5. Third-Party Services",
    p: [
      "To deliver certain features we rely on trusted third-party service providers who process data on our behalf under appropriate safeguards:",
    ],
    list: [
      "An email delivery provider, used to send us your enquiry so we can respond.",
      "An AI language-model provider, used solely to generate responses within the on-site advisory chat.",
      "Secure database hosting used to store enquiries, accounts and chat history.",
    ],
    p2: [
      "These providers only process the information necessary to perform their function and are not permitted to use it for their own purposes.",
    ],
  },
  {
    h: "6. Data Retention",
    p: [
      "We keep personal information only for as long as necessary to respond to your enquiry, provide our services, comply with legal obligations, and resolve disputes. Enquiries and chat records may be reviewed and deleted by us periodically. You may request deletion of your information at any time (see \"Your Rights\").",
    ],
  },
  {
    h: "7. Data Security",
    p: [
      "We take reasonable technical and organisational measures to protect your information, including transport encryption (HTTPS), password hashing, and access controls that restrict who can view enquiries and chat records. No method of transmission or storage is completely secure, but we work to protect your data and continually review our safeguards.",
    ],
  },
  {
    h: "8. Your Rights",
    p: [
      "Depending on your location, you may have rights over your personal information, including the right to access, correct, delete or restrict its use, and to object to certain processing or withdraw consent. To exercise any of these rights, please contact us using the details below and we will respond within a reasonable timeframe.",
    ],
  },
  {
    h: "9. Important Note on Advisory Content",
    p: [
      "Information provided through this website and the advisory chat is general, high-level guidance only and does not constitute a formal security assessment, operational instruction or professional engagement. Bespoke advice is provided only under a formal consultancy engagement with Arco Orbis2.",
    ],
  },
  {
    h: "10. Changes to This Policy",
    p: [
      "We may update this Privacy Policy from time to time to reflect changes in our practices or for legal reasons. The latest version will always be available on this page with the date it was last updated.",
    ],
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="grain bg-ink text-white min-h-screen antialiased">
      <Navbar />
      <main className="px-6 md:px-10 lg:px-16 pt-36 pb-24 md:pt-40">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-silver/60 hover:text-gold transition-colors text-sm font-body mb-8" data-testid="privacy-back">
            <ArrowLeft size={16} className="rtl-flip" /> Back to home
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="overline text-gold mb-5 flex items-center gap-3">
              <ShieldCheck size={16} /> Your Privacy Matters
            </div>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Privacy <span className="gold-text">Policy</span>
            </h1>
            <p className="mt-5 text-silver/50 font-body text-sm">Last updated: {UPDATED}</p>
          </motion.div>

          <div className="mt-12 space-y-12">
            {SECTIONS.map((s, i) => (
              <motion.section
                key={s.h}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.05 }}
                data-testid={`privacy-section-${i}`}
              >
                <h2 className="font-display font-light text-2xl text-white">{s.h}</h2>
                <div className="mt-4 h-px w-10 bg-gold/40" />
                {s.p && s.p.map((para, j) => (
                  <p key={j} className="mt-4 text-silver/70 font-body font-light text-sm md:text-base leading-relaxed">{para}</p>
                ))}
                {s.list && (
                  <ul className="mt-4 space-y-2.5">
                    {s.list.map((li) => (
                      <li key={li} className="flex gap-3 text-silver/70 font-body font-light text-sm md:text-base leading-relaxed">
                        <span className="mt-2 h-1 w-1 rounded-full bg-gold shrink-0" /> {li}
                      </li>
                    ))}
                  </ul>
                )}
                {s.p2 && s.p2.map((para, j) => (
                  <p key={j} className="mt-4 text-silver/70 font-body font-light text-sm md:text-base leading-relaxed">{para}</p>
                ))}
              </motion.section>
            ))}

            {/* Contact block */}
            <motion.section
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="hairline rounded-2xl bg-ink-surface/60 p-7"
              data-testid="privacy-contact"
            >
              <h2 className="font-display font-light text-2xl text-white">11. Contact Us</h2>
              <p className="mt-4 text-silver/70 font-body font-light text-sm md:text-base leading-relaxed">
                If you have any questions about this Privacy Policy or how your information is handled, please contact Arco Orbis2:
              </p>
              <div className="mt-5 space-y-2">
                {emails.map((e) => (
                  <a key={e} href={`mailto:${e}`} className="block font-body text-sm text-gold hover:underline" data-testid="privacy-email">{e}</a>
                ))}
                {address && <p className="font-body text-sm text-silver/70">{address}</p>}
              </div>
            </motion.section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
